import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import BillingDetailCard from "../components/BillingDetailCard";
import BillingPageHeader from "../components/BillingPageHeader";
import BillingStateCard from "../components/BillingStateCard";
import { createSubscriptionPlansService } from "../services/subscriptionPlansService";
import { useBillingApiClient } from "../services/useBillingApiClient";
import { getSubscriptionPlanDetails } from "./subscriptionPlansConfig";
import "./SubscriptionPlanDetailPage.css";

const SubscriptionPlanDetailPage = () => {
  const { Id } = useParams();
  const apiClient = useBillingApiClient();
  const subscriptionPlansService = useMemo(
    () => createSubscriptionPlansService(apiClient),
    [apiClient]
  );
  const [plan, setPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchPlan = async () => {
      try {
        setIsLoading(true);
        const response = await subscriptionPlansService.getById(Id);

        if (!isMounted) {
          return;
        }

        setPlan(response);
        setErrorMessage("");
      } catch (error) {
        if (isMounted) {
          setErrorMessage(
            error?.response?.data?.message ||
              "Unable to load the selected subscription plan."
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchPlan();

    return () => {
      isMounted = false;
    };
  }, [Id, subscriptionPlansService]);

  if (isLoading) {
    return (
      <BillingStateCard
        variant="loading"
        title="Loading subscription plan"
        message="Fetching the latest subscription plan details."
      />
    );
  }

  if (errorMessage || !plan) {
    return (
      <BillingStateCard
        variant="error"
        title="Plan details unavailable"
        message={errorMessage || "No subscription plan data was returned."}
        actionLabel="Back to Plans"
        onAction={() => window.history.back()}
      />
    );
  }

  return (
    <div className="subscription-plan-detail-page">
      <BillingPageHeader
        title={plan.plan_name || "Subscription Plan"}
        description="Detailed billing and service mapping information for this subscription plan."
      />

      <BillingDetailCard
        title="Plan Details"
        sections={getSubscriptionPlanDetails(plan)}
        actions={
          <>
            <Link
              className="subscription-plan-detail-page__action subscription-plan-detail-page__action--ghost"
              to="/subscription-plans"
            >
              Back to Plans
            </Link>
            <Link
              className="subscription-plan-detail-page__action"
              to={`/subscription-plans/${plan.id}/edit`}
            >
              Edit Plan
            </Link>
          </>
        }
      />
    </div>
  );
};

export default SubscriptionPlanDetailPage;
